import React, {useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {Button} from 'primereact/button';
import {InputText} from 'primereact/inputtext';
import {Checkbox} from 'primereact/checkbox';
import {Dialog} from 'primereact/dialog';
import {ProgressSpinner} from 'primereact/progressspinner';
import {connectionPath, isFullyMatched, itemLinks, matchEdges} from './inventoryReceiptMatchUtils';
import './InventoryReceiptMatchGraph.css';

export default function InventoryReceiptMatchGraph({items, requests, t, formatQuantity, busy, selected, onSelectionChange, onEdit, onUnlink}) {
  const root = useRef(null);
  const [search, setSearch] = useState('');
  const [incomplete, setIncomplete] = useState(false);
  const [geometry, setGeometry] = useState([]);
  const [highlight, setHighlight] = useState(null);
  const [details, setDetails] = useState(null);
  const [pendingJump, setPendingJump] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);
  const edges = useMemo(() => matchEdges(requests), [requests]);
  const matched = row => itemLinks(row).reduce((sum, link) => sum + Number(link.quantity || 0), 0);
  const matchesSearch = text => String(text || '').toLocaleLowerCase().includes(search.trim().toLocaleLowerCase());
  const nonZeroItems = items.filter(row => Number(row.quantity) !== 0);
  const leftRows = nonZeroItems.filter(row => matchesSearch(row.material?.name) && (!incomplete || !isFullyMatched(row.quantity, row.matched_quantity)));
  const rightRows = requests.filter(row => matchesSearch(row.description) && (!incomplete || !isFullyMatched(row.quantity, matched(row))));
  const related = (side, id) => edges.filter(edge => edge[side] === String(id));
  const highlighted = edge => !highlight || (highlight.key ? edge.key === highlight.key : edge[highlight.side] === String(highlight.id));
  const rowActive = (side, id) => !highlight || (highlight.side === side && String(highlight.id) === String(id)) ||
    edges.some(edge => edge[side] === String(id) && highlighted(edge));

  useLayoutEffect(() => {
    const element = root.current;
    if (!element) return;
    let frame;
    const measure = () => {
      const box = element.getBoundingClientRect();
      if (box.width < 850) { setGeometry([]); return; }
      const anchors = {};
      element.querySelectorAll('[data-match-side]').forEach(viewport => {
        const bounds = viewport.getBoundingClientRect();
        viewport.querySelectorAll('[data-match-id]').forEach(row => {
          const rect = row.getBoundingClientRect();
          const center = rect.top + rect.height / 2;
          const headerHeight = viewport.querySelector('thead')?.getBoundingClientRect().height || 0;
          if (center < bounds.top + headerHeight || center > bounds.bottom) return;
          anchors[viewport.dataset.matchSide + ':' + row.dataset.matchId] = {
            x: (viewport.dataset.matchSide === 'left' ? bounds.right : bounds.left) - box.left,
            y: center - box.top
          };
        });
      });
      const visible = edges.flatMap(edge => {
        const left = anchors['left:' + edge.left], right = anchors['right:' + edge.right];
        return left && right ? [{...edge, x1: left.x, y1: left.y, x2: right.x, y2: right.y, labelY: (left.y + right.y) / 2}] : [];
      }).sort((a, b) => a.labelY - b.labelY || a.key.localeCompare(b.key));
      // Keep labels separately clickable, even for split quantities on adjacent lines.
      visible.forEach((edge, index) => {
        if (index) edge.labelY = Math.max(edge.labelY, visible[index - 1].labelY + 30);
      });
      for (let index = visible.length - 1; index >= 0; index--) {
        visible[index].labelY = Math.min(visible[index].labelY, index === visible.length - 1 ? box.height - 18 : visible[index + 1].labelY - 30);
      }
      setGeometry(visible.filter(edge => edge.labelY > 18));
    };
    const schedule = () => { cancelAnimationFrame(frame); frame = requestAnimationFrame(measure); };
    const observer = new ResizeObserver(schedule);
    observer.observe(element);
    element.querySelectorAll('[data-match-side]').forEach(viewport => observer.observe(viewport));
    element.addEventListener('scroll', schedule, true);
    window.addEventListener('resize', schedule);
    schedule();
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      element.removeEventListener('scroll', schedule, true);
      window.removeEventListener('resize', schedule);
    };
  }, [edges, search, incomplete, items, busy]);

  useEffect(() => {
    if (!pendingJump) return;
    const viewport = root.current?.querySelector('[data-match-side="' + pendingJump.side + '"]');
    const row = Array.from(viewport?.querySelectorAll('[data-match-id]') || []).find(node => node.dataset.matchId === String(pendingJump.id));
    row?.scrollIntoView({block: 'center', behavior: 'smooth'});
    row?.focus({preventScroll: true});
    setPendingJump(null);
  }, [pendingJump, search, incomplete]);

  const jump = (side, id) => {
    setDetails(null);
    setSearch('');
    setIncomplete(false);
    setHighlight({side, id});
    setPendingJump({side, id});
  };
  const showLinks = (side, id) => { setDetails({side, id}); setHighlight({side, id}); };
  const detailEdges = details?.key ? edges.filter(edge => edge.key === details.key) : details ? related(details.side, details.id) : [];
  const connectionButton = (side, row) => {
    const count = related(side, row.id).length;
    return count > 0 && <Button className="p-button-text p-button-sm match-link-count" icon="pi pi-link"
      label={t('itemMatchConnections', {count})} disabled={busy} onClick={() => showLinks(side, row.id)}/>;
  };
  const rowClass = (side, row, amount) => [
    isFullyMatched(row.quantity, amount) ? 'match-complete' : '',
    !rowActive(side, row.id) ? 'match-dimmed' : '',
    dropTarget === row.id && side === 'left' ? 'match-drop-target' : ''
  ].join(' ');
  const drop = (event, target) => {
    event.preventDefault();
    setDropTarget(null);
    if (busy) return;
    try {
      const payload = JSON.parse(event.dataTransfer.getData('application/json'));
      if (payload.source === 'purchase-order-request-items' && Array.isArray(payload.ids)) onEdit(target, payload.ids);
    } catch (_) { /* Ignore unrelated native drags. */ }
  };

  return <section className="inventory-receipt-matches" aria-busy={busy}>
    <div className="match-toolbar">
      <InputText value={search} onChange={event => setSearch(event.target.value)} placeholder={t('itemMatchSearch')} aria-label={t('itemMatchSearch')}/>
      <label className="match-filter"><Checkbox checked={incomplete} onChange={event => setIncomplete(event.checked)}/>{t('itemMatchIncomplete')}</label>
      {busy && <ProgressSpinner style={{width: 28, height: 28}}/>}
    </div>
    <p className="match-hint">{t('itemMatchGraphHelp')}</p>
    <div className="match-board" ref={root}>
      <div className="match-list" data-match-side="left">
        <table><thead><tr><th colSpan={2}>{t('deliveryNoteItems')} <small>({leftRows.length}/{nonZeroItems.length})</small></th></tr>
          <tr><th>{t('columnName')}</th><th className="match-number">{t('columnQuantity')}</th></tr></thead>
          <tbody>{leftRows.map(row => <tr key={row.id} data-match-id={row.id} tabIndex={-1} className={rowClass('left', row, row.matched_quantity)}
            onMouseEnter={() => setHighlight({side: 'left', id: row.id})} onMouseLeave={() => setHighlight(null)}
            onDragOver={event => { if (!busy) { event.preventDefault(); setDropTarget(row.id); } }} onDragLeave={() => setDropTarget(null)} onDrop={event => drop(event, row)}>
            <td><strong>{row.material?.name}</strong><div>{connectionButton('left', row)}
              <Button className="p-button-text p-button-sm" icon="pi pi-pencil" label={t('itemMatchTitle')} disabled={busy} onClick={() => onEdit(row, selected.map(item => item.id))}/></div></td>
            <td className="match-number">{formatQuantity(row.quantity)} {row.unit?.name}<small>{t('itemMatchLinked', {quantity: formatQuantity(row.matched_quantity || 0)})}</small></td>
          </tr>)}
          {!leftRows.length && <tr><td colSpan={2}>{t('noResults')}</td></tr>}</tbody></table>
      </div>
      <div className="match-gutter" aria-hidden="true"/>
      <div className="match-list" data-match-side="right">
        <table><thead><tr><th colSpan={3}>{t('selectedInvoiceItems')} <small>({rightRows.length}/{requests.length})</small></th></tr>
          <tr><th>{t('columnName')}</th><th className="match-number">{t('columnQuantity')}</th><th className="match-number">{t('columnNetTotal')}</th></tr></thead>
          <tbody>{rightRows.map(row => <tr key={row.id} data-match-id={row.id} tabIndex={-1} className={rowClass('right', row, matched(row))}
            draggable={!busy} onDragStart={event => {
              const ids = selected.some(item => item.id === row.id) ? selected.map(item => item.id) : [row.id];
              event.dataTransfer.setData('application/json', JSON.stringify({source: 'purchase-order-request-items', ids}));
              event.dataTransfer.effectAllowed = 'move';
            }} onDragEnd={() => setDropTarget(null)}
            onMouseEnter={() => setHighlight({side: 'right', id: row.id})} onMouseLeave={() => setHighlight(null)}>
            <td><div className="match-name"><Checkbox aria-label={row.description} disabled={busy} checked={selected.some(item => item.id === row.id)}
              onChange={event => onSelectionChange(event.checked ? [...selected, row] : selected.filter(item => item.id !== row.id))}/><strong>{row.description}</strong></div>
              {connectionButton('right', row)}</td>
            <td className="match-number">{formatQuantity(row.quantity)} {row.unit?.name}<small>{t('itemMatchLinked', {quantity: formatQuantity(matched(row))})}</small></td>
            <td className="match-number">{window.App.currencyFormatter(row.currency || row.receipt?.currency || 'HUF', row.net_amount)}</td>
          </tr>)}
          {!rightRows.length && <tr><td colSpan={3}>{t('noResults')}</td></tr>}</tbody></table>
      </div>
      <svg className="match-curves" aria-hidden="true">{geometry.map(edge => <path key={edge.key}
        className={highlighted(edge) ? 'match-curve' : 'match-curve match-dimmed'}
        d={connectionPath(edge.x1, edge.y1, edge.x2, edge.y2, edge.labelY)}/>)}</svg>
      <div className="match-labels">{geometry.map(edge => <button type="button" key={edge.key}
        className={'match-edge-label ' + (highlighted(edge) ? '' : 'match-dimmed')} disabled={busy}
        style={{left: (edge.x1 + edge.x2) / 2, top: edge.labelY}}
        title={edge.link.material?.name + ' → ' + edge.receipt.description}
        aria-label={t('itemMatchEdgeLabel', {from: edge.link.material?.name, to: edge.receipt.description, quantity: formatQuantity(edge.link.quantity), unit: edge.link.unit?.name || ''})}
        onMouseEnter={() => setHighlight({key: edge.key})} onMouseLeave={() => setHighlight(null)}
        onFocus={() => setHighlight({key: edge.key})} onBlur={() => setHighlight(null)}
        onClick={() => setDetails({key: edge.key})}>{formatQuantity(edge.link.quantity)} {edge.link.unit?.name}</button>)}</div>
    </div>
    <Dialog header={t('linkedDeliveryNoteItems')} visible={!!details} onHide={() => { setDetails(null); setHighlight(null); }} style={{width: 520, maxWidth: '95vw'}}>
      {detailEdges.map(edge => {
        const target = items.find(item => String(item.id) === edge.left);
        return <div className="match-detail" key={edge.key}>
          <strong>{edge.link.material?.name}</strong>
          <div>{edge.link.inventory_document?.document_number}</div>
          <p>→ {edge.receipt.description}</p>
          <strong>{formatQuantity(edge.link.quantity)} {edge.link.unit?.name}</strong>
          <div className="match-detail-actions">
            {target && <Button className="p-button-text" label={t('itemMatchJumpDelivery')} onClick={() => jump('left', target.id)}/>}
            <Button className="p-button-text" label={t('itemMatchJumpInvoice')} onClick={() => jump('right', edge.receipt.id)}/>
            {target && <Button label={t('itemMatchTitle')} disabled={busy} onClick={() => { setDetails(null); onEdit(target); }}/>}
            <Button className="p-button-danger p-button-text" icon="pi pi-times" label={t('itemMatchUnlink')} disabled={busy}
              onClick={() => { setDetails(null); onUnlink(edge.link.id, edge.receipt.id); }}/>
          </div>
        </div>;
      })}
    </Dialog>
  </section>;
}
